import {
  userIsCompanySuperuser,
  userIsTenantAdmin,
} from '@/utils/userRoles';
import { isCommitmentOverdue } from '@/utils/userProjectsOverview';

export function averagePpcFromHistory(history = []) {
  const values = (history || [])
    .map((entry) => entry?.ppc)
    .filter((ppc) => typeof ppc === 'number');
  if (!values.length) return null;
  return Math.round(values.reduce((sum, ppc) => sum + ppc, 0) / values.length);
}

export function resolvePpcAmpelHex(ppc, thresholds = { greenMin: 80, yellowMin: 60 }) {
  if (ppc === null || ppc === undefined) return null;
  if (ppc >= (thresholds.greenMin ?? 80)) return '#22C55E';
  if (ppc >= (thresholds.yellowMin ?? 60)) return '#EAB308';
  return '#EF4444';
}

export function formatPpcDelta(delta, unit = '%') {
  if (delta === null || delta === undefined || Number.isNaN(Number(delta))) return null;
  const value = Number(delta);
  const sign = value > 0 ? '+' : '';
  return `${sign}${value}${unit}`;
}

/**
 * Milestone chip counts for cockpit KPI tile (✓ / ⚠ / ✗).
 * Uses ampel from calculateMilestoneDisplayStatus-style input.
 */
export function countMilestoneChips(milestones = []) {
  const counts = { green: 0, yellow: 0, red: 0 };
  for (const milestone of milestones) {
    const ampel = milestone?.ampel || milestone?.displayAmpel || 'green';
    if (ampel === 'yellow') counts.yellow += 1;
    else if (ampel === 'red') counts.red += 1;
    else counts.green += 1;
  }
  return counts;
}

export function openConstraintsCount(constraints = []) {
  return (constraints || []).filter(
    (item) => item.status === 'open' || item.status === 'in_progress',
  ).length;
}

/**
 * Mockup MeilensteinListe: active milestones show PPC + commitment roll-up.
 * Active = on_track / at_risk / in_progress (not done, not future open).
 */
export function isActiveMilestoneForCockpit(milestone = {}) {
  if (!milestone || milestone.status === 'done' || milestone.actualDate) {
    return false;
  }
  const status = milestone.status || '';
  if (status === 'on_track' || status === 'at_risk' || status === 'in_progress') {
    return true;
  }
  const ampel = milestone.ampel || milestone.displayAmpel;
  return ampel === 'yellow' || ampel === 'red';
}

/**
 * Commitment stats for one milestone (linked via milestoneId).
 * @returns {{ ppc: number | null, done: number, total: number, open: number, overdue: number } | null}
 */
export function summarizeMilestoneCommitments(commitments = [], milestoneId, now = new Date()) {
  if (!milestoneId) return null;
  const id = String(milestoneId);
  const linked = (commitments || []).filter(
    (item) => item?.milestoneId && String(item.milestoneId) === id,
  );
  if (!linked.length) {
    return {
      ppc: null,
      done: 0,
      total: 0,
      open: 0,
      overdue: 0,
    };
  }

  let done = 0;
  let open = 0;
  let overdue = 0;
  for (const item of linked) {
    if (item.status === 'done') {
      done += 1;
      continue;
    }
    open += 1;
    if (item.isOverdue || isCommitmentOverdue(item.dueDate, now)) {
      overdue += 1;
    }
  }
  const total = linked.length;
  return {
    ppc: total > 0 ? Math.round((done / total) * 100) : null,
    done,
    total,
    open,
    overdue,
  };
}

/**
 * Rough week-over-week open-constraint delta when previous count is known.
 */
export function constraintTrendDelta(currentOpen, previousOpen) {
  if (typeof currentOpen !== 'number' || typeof previousOpen !== 'number') return null;
  return currentOpen - previousOpen;
}

export function isCockpitFullView({ user, selectedProject }) {
  if (!user) return false;
  if (userIsTenantAdmin(user) || userIsCompanySuperuser(user)) {
    return true;
  }
  const leadId = selectedProject?.projectLeadUserId
    ? String(selectedProject.projectLeadUserId)
    : '';
  const userId = user._id ? String(user._id) : '';
  return Boolean(leadId && userId && leadId === userId);
}
